/**
 * **The card** (F3): one person found — who, today, the waiver, why here, the family, the tag,
 * and the big button.
 */
import { type ReactNode } from 'react';
import { formatCountDay, formatDate, formatPhone, formatTime } from '@cider/ui';
import { OperatorName, PlaceOn } from './BoothDesk.js';
import { AgeRow, EmergencyRow } from './BoothScan.js';
import { first, hours, worked } from './BoothShared.js';
import { useBooth } from './BoothState.js';

/** The one card: who, today, the tag, and Check in. */
export function PersonCard(): ReactNode {
  const {
    synced,
    picked,
    setPicked,
    placeOn,
    setPlaceOn,
    already,
    setDone,
    setTag,
    busy,
    guest,
    waiverOf,
    needsWaiver,
    queue,
    offline,
    operator,
    setOperator,
    desk,
    opName,
    why,
    setWhy,
    issueItem,
    setIssueItem,
    issueNumber,
    setIssueNumber,
    choices,
    serviceGroup,
    setServiceGroup,
    creditPick,
    setCreditPick,
    holding,
    setHolding,
    hand,
    setHand,
    show,
    answerWhy,
    linkAndCheckIn,
    reprint,
    reset,
    act,
    shortest,
    placeChoices,
    checkIn,
    expectedShift,
    tagless,
    inShift,
    isChair,
    tablet,
    tooYoung,
    instead,
    signedHere,
    teenWaits,
    WHY_WORDS,
    linkedFamily,
    schoolFamily,
    toLink,
    missing,
  } = useBooth();
  if (!picked || !desk) return null;
  return (
    <>
      <div className="booth__found" data-testid="booth-card">
        {picked.today.length === 0 && !picked.quiet && !isChair && picked.otherDays[0] ? (
          <div
            className="booth__banner booth__banner--warn"
            role="status"
            data-testid="booth-notonday"
          >
            <b>
              {first(picked.name).toUpperCase()} IS ON{' '}
              {formatCountDay(picked.otherDays[0].day).toUpperCase()} ·{' '}
              {picked.otherDays[0].area.toUpperCase()} ·{' '}
              {hours(picked.otherDays[0].startsAt, picked.otherDays[0].endsAt)}
            </b>
            <span>
              Not on today. They can help now from standby, or you can put them on a shift today.
            </span>
          </div>
        ) : null}
        {offline ? (
          <section className="booth__card booth__card--gold" data-testid="booth-offline-card">
            <div className="booth__cardhead">
              NO SERVER
              <span className="booth__cardsub">
                the day kept on this station
                {synced ? ` · last sync ${formatTime(synced)}` : ''}
              </span>
            </div>
            <div className="booth__row booth__row--warn">
              <span className="booth__rowa">
                <b>Check-in</b>
                <span className="booth__rowb">
                  queued · {queue.length + 1}
                  {queue.length + 1 === 1
                    ? 'st'
                    : queue.length + 1 === 2
                      ? 'nd'
                      : queue.length + 1 === 3
                        ? 'rd'
                        : 'th'}
                </span>
              </span>
              <span className="booth__rowc">
                Sent with its time the moment the server answers; the tag prints now.
              </span>
              <span className="booth__chip booth__chip--warn">QUEUED</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>This year’s waiver</b>
                <span className="booth__rowb">needs the server</span>
              </span>
              <span className="booth__rowc">
                Someone who has not signed signs when the server answers — a moment.
              </span>
            </div>
          </section>
        ) : null}
        {picked.quiet ? (
          <div className="booth__banner" role="status" data-testid="booth-quiet">
            <b>SEE THE BOOTH CHAIR</b>
            <span>
              Call the booth chair before anything else. Nothing else happens at the desk for{' '}
              {first(picked.name)} until they do; the guest screen shows nothing.
            </span>
          </div>
        ) : null}
        {tooYoung ? (
          <div
            className="booth__banner booth__banner--bad"
            role="alert"
            data-testid="booth-too-young"
          >
            <b>
              HARD STOP · UNDER {tooYoung.min} IN THE {tooYoung.area.toUpperCase()}
              {tooYoung.min >= 21 ? ' (STATE LAW)' : ''}
            </b>
            <span>
              {first(picked.name)} is {tooYoung.age}. They cannot work the {tooYoung.area}. No
              Proceed anyway.
            </span>
          </div>
        ) : null}
        {tooYoung && expectedShift ? (
          <section className="booth__card booth__card--red" data-testid="booth-instead">
            <div className="booth__cardhead">
              WHERE INSTEAD
              <span className="booth__cardsub">short now, same hours</span>
            </div>
            {instead.length ? (
              instead.map((x) => (
                <div className="booth__row" key={x.shiftId}>
                  <span className="booth__rowa">
                    <b>
                      {x.area} · {hours(x.startsAt, x.endsAt)}
                    </b>
                    <span className="booth__rowb">
                      {x.title ? `${x.title} · ` : ''}short {x.short}
                    </span>
                  </span>
                  <span className="booth__rowc">Same time. Both areas’ chairs are told.</span>
                  <span className="booth__chip booth__chip--warn">SHORT {x.short}</span>
                  <button
                    type="button"
                    className="booth__act"
                    disabled={busy || !opName.trim()}
                    onClick={() => {
                      const who = picked;
                      act(
                        '/volunteers/booth/reassign',
                        {
                          fromShiftId: expectedShift.shiftId,
                          personId: who.personId,
                          toShiftId: x.shiftId,
                          name: opName,
                          reason: null,
                        },
                        (res) => {
                          setDone(`${who.name}: ${res.says}`);
                          show('tag', who.personId, x.shiftId);
                        },
                      );
                    }}
                  >
                    Move them
                  </button>
                </div>
              ))
            ) : (
              <div className="booth__row">
                <span className="booth__rowc">
                  No shift at the same hours is short right now — see the booth chair.
                </span>
              </div>
            )}
            <div className="booth__cardfoot">
              How they got on the {tooYoung.area} roster is a question for the volunteer chair — the
              roster warns, the booth stops.
            </div>
          </section>
        ) : null}
        {already?.personId === picked.personId ? (
          <div className="booth__banner" role="status" data-testid="booth-already">
            <b>ALREADY CHECKED IN{already.at ? ` · ${formatTime(already.at)}` : ''}</b>
            <span>
              A tag scanned
              {already.at
                ? ` ${String(Math.max(0, Math.round((Date.now() - new Date(already.at).getTime()) / 60_000)))} minutes`
                : ''}{' '}
              after check-in isn’t a check-out. Nothing changes.
            </span>
          </div>
        ) : null}
        {teenWaits ? (
          <div className="booth__banner booth__banner--bad" role="alert" data-testid="booth-teen">
            <b>HARD STOP · THE TEEN RULE (PARISH POLICY)</b>
            <span>
              A working member aged 14 to 17 checks in with a parent’s signature: a parent at the
              booth signs on the guest screen. This one has no Proceed anyway.
            </span>
          </div>
        ) : null}
        <section className="booth__card booth__card--ok">
          <div className="booth__cardhead">
            {picked.name.toUpperCase()}
            <span className="booth__cardsub">
              {[
                picked.household
                  ? `the ${picked.household} household`
                  : 'no household on the record',
                picked.phone ? formatPhone(picked.phone) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          {picked.quiet ? null : (
            <>
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>WHO</b>
                  <span className="booth__rowb">
                    {picked.name}
                    {picked.minor && picked.age !== null && picked.age !== undefined
                      ? `, ${String(picked.age)}`
                      : ''}
                    {picked.household ? ` · the ${picked.household} household` : ''}
                  </span>
                </span>
                <span className="booth__rowc">
                  If the person in front of you is not {first(picked.name)} — family, a friend — tap
                  Someone else. The shift moves to whoever is working it.
                </span>
                <span className={`booth__chip booth__chip--${guest?.confirmed ? 'ok' : 'info'}`}>
                  {guest?.on && guest.confirmed
                    ? 'SAID YES'
                    : guest?.on && guest.state === 'confirm'
                      ? 'ASKED ON THE GUEST SCREEN'
                      : `IS THIS ${first(picked.name).toUpperCase()}?`}
                </span>
                {expectedShift?.signupId ? (
                  <button
                    type="button"
                    className="booth__act"
                    onClick={() => {
                      setHand({
                        kind: 'swap',
                        shiftId: expectedShift.shiftId,
                        signupId: expectedShift.signupId ?? '',
                      });
                    }}
                  >
                    Someone else
                  </button>
                ) : null}
              </div>
              {picked.today.map((s) => (
                <div className="booth__row" key={s.shiftId}>
                  <span className="booth__rowa">
                    <b>TODAY</b>
                    <span className="booth__rowb">
                      {s.area} · {hours(s.startsAt, s.endsAt)}
                      {s.title ? ` · ${s.title}` : ''}
                    </span>
                  </span>
                  <span className="booth__rowc">
                    {picked.today.length === 1
                      ? 'Only shift today.'
                      : `${String(picked.today.length)} shifts today.`}
                  </span>
                  {s.workedBy ? (
                    <span className="booth__chip booth__chip--ok">
                      WORKED BY {s.workedBy.toUpperCase()}
                    </span>
                  ) : (
                    <span
                      className={`booth__chip booth__chip--${s.state === 'expected' ? 'info' : s.state === 'in' ? 'ok' : 'mute'}`}
                    >
                      {s.state === 'expected'
                        ? picked.today.length === 1
                          ? 'ONE SHIFT'
                          : 'EXPECTED'
                        : s.state === 'in'
                          ? 'CHECKED IN'
                          : 'CHECKED OUT'}
                    </span>
                  )}
                  {!s.workedBy && s.state !== 'out' ? (
                    <button
                      type="button"
                      className="booth__act"
                      onClick={() => {
                        setWhy('');
                        setHand({
                          kind: 'reassign',
                          shiftId: s.shiftId,
                          to:
                            placeChoices.find((x) => x.shiftId !== s.shiftId && x.short > 0)
                              ?.shiftId ?? '',
                        });
                      }}
                    >
                      Reassign
                    </button>
                  ) : null}
                </div>
              ))}
              <div className="booth__row" data-testid="booth-family">
                <span className="booth__rowa">
                  <b>WHY HERE</b>
                  <span className="booth__rowb">
                    {picked.whyHere
                      ? `${WHY_WORDS[picked.whyHere.reason] ?? picked.whyHere.reason}${picked.whyHere.serviceGroup ? ` · ${picked.whyHere.serviceGroup}` : ''} · answered ${formatDate(picked.whyHere.on)}`
                      : 'not asked yet this year'}
                  </span>
                </span>
                <span className="booth__rowc">
                  {picked.whyHere
                    ? schoolFamily
                      ? linkedFamily
                        ? `For ${linkedFamily.responsibleParty}’s family — credit follows the check-out.`
                        : 'The family must be linked before the check-in counts.'
                      : 'Asked once a year. No commitment.'
                    : 'Asked once a year — on the guest screen, or here.'}
                </span>
                <span className={`booth__chip booth__chip--${picked.whyHere ? 'ok' : 'warn'}`}>
                  {picked.whyHere ? 'ANSWERED' : 'ASK'}
                </span>
              </div>
              <div
                className={`booth__row${missing.length ? ' booth__row--warn' : ''}`}
                data-testid="booth-missing"
              >
                <span className="booth__rowa">
                  <b>MISSING</b>
                  <span className="booth__rowb">
                    {missing.length ? missing.join(' · ') : 'nothing'}
                  </span>
                </span>
                <span className="booth__rowc" data-testid="booth-waiver-state">
                  {[
                    needsWaiver
                      ? signedHere
                        ? 'Waiver signed on the guest screen just now'
                        : 'Waiver not signed this year — they sign on the guest screen'
                      : waiverOf?.signedOn
                        ? `Waiver signed ${formatDate(waiverOf.signedOn)}`
                        : 'No waiver this year',
                    picked.credentials?.length
                      ? picked.credentials.join(' · ')
                      : 'no credential needed',
                    picked.minor
                      ? 'under 18 — a parent signs (DRAFT, wording pending)'
                      : 'not a minor',
                    picked.emergency === false ? 'no emergency contact on the record' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  .
                </span>
                <span className={`booth__chip booth__chip--${missing.length ? 'warn' : 'ok'}`}>
                  {missing.length ? `${String(missing.length)} TO DO` : 'CLEAR'}
                </span>
                {needsWaiver && !signedHere ? (
                  <button
                    type="button"
                    className="booth__act"
                    data-testid="booth-waiver"
                    onClick={() => {
                      show('waiver', picked.personId, picked.today[0]?.shiftId ?? null);
                    }}
                  >
                    Put the waiver on the guest screen
                  </button>
                ) : null}
              </div>
              {picked.emergency === false ? (
                <EmergencyRow
                  personId={picked.personId}
                  minor={picked.minor ?? false}
                  onDone={(says) => {
                    setDone(says);
                    setPicked({ ...picked, emergency: true });
                  }}
                  onSkipped={(says) => {
                    setDone(says);
                    setPicked({ ...picked, emergency: true });
                  }}
                />
              ) : null}
              {picked.ageKnown === false ? (
                <AgeRow
                  personId={picked.personId}
                  onDone={(says, age) => {
                    setDone(says);
                    setPicked({
                      ...picked,
                      ageKnown: true,
                      age,
                      minor: age !== null ? age < 18 : false,
                    });
                  }}
                />
              ) : null}
              {picked.issue.eligible && !tablet ? (
                <div className="booth__row">
                  <span className="booth__rowa">
                    <b>ISSUE</b>
                    <span className="booth__rowb">
                      {picked.issue.out.length
                        ? picked.issue.out.map((o) => `${o.itemName} ${o.number}`).join(' · ')
                        : 'nothing out'}
                    </span>
                  </span>
                  <span className="booth__rowc">Radios and keys, one tap each, in your name.</span>
                  <span className="booth__chip booth__chip--info">ISSUE</span>
                </div>
              ) : null}
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>TAG</b>
                  <span className="booth__rowb">
                    {tagless
                      ? `the ${expectedShift?.area ?? ''} does not use tags`
                      : tablet
                        ? 'no printer here'
                        : 'prints at this station'}
                  </span>
                </span>
                <span className="booth__chip">
                  {tagless ? 'NO TAG' : tablet ? 'AT THE BOOTH' : 'WILL PRINT'}
                </span>
              </div>
            </>
          )}
        </section>
        {inShift && !expectedShift && !hand ? (
          <section className="booth__card booth__card--ok" data-testid="booth-checkout">
            <div className="booth__cardhead">
              {picked.name.toUpperCase()}
              <span className="booth__cardsub">
                {inShift.area} · {hours(inShift.startsAt, inShift.endsAt)}
                {inShift.inAt ? ` · in since ${formatTime(inShift.inAt)}` : ''}
              </span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Check out by name</b>
                <span className="booth__rowb">no tag needed</span>
              </span>
              <span className="booth__rowc">Same check-out as the QR. Credit awarded now.</span>
            </div>
            {picked.today.some((x) => x.state === 'expected' && x !== inShift) ? (
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>Two shifts today</b>
                  <span className="booth__rowb">
                    {picked.today
                      .map((x) => `${x.area} ${hours(x.startsAt, x.endsAt)}`)
                      .join(' · ')}
                  </span>
                </span>
                <span className="booth__rowc">
                  Checking out of the {inShift.area}; {first(picked.name)} is still expected at the
                  next.
                </span>
                <span className="booth__chip booth__chip--info">NEXT</span>
              </div>
            ) : null}
          </section>
        ) : null}
        {teenWaits ? (
          <section className="booth__card booth__card--ok" data-testid="booth-teen-card">
            <div className="booth__cardhead">
              {picked.name.toUpperCase()}
              <span className="booth__cardsub">
                {expectedShift
                  ? `${expectedShift.area} · ${hours(expectedShift.startsAt, expectedShift.endsAt)}`
                  : 'under 18'}
              </span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>A parent at the booth?</b>
                <span className="booth__rowb">
                  {guest?.on && guest.state === 'waiver'
                    ? 'the waiver is on the guest screen for the parent'
                    : 'put the waiver on the guest screen for them'}
                </span>
              </span>
              <span className="booth__rowc">
                The parent says so on the guest screen, gives their name, reads and signs. The
                record names them.
              </span>
              <button
                type="button"
                className="booth__act"
                onClick={() => {
                  show('waiver', picked.personId, expectedShift?.shiftId ?? null);
                }}
              >
                A parent is here
              </button>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>A parent’s signature ahead of time?</b>
                <span className="booth__rowb">not on the family page yet</span>
              </span>
              <span className="booth__rowc">
                Signing ahead on the family page lands with the family page’s waiver card.
              </span>
              <span className="booth__chip">NOT YET</span>
            </div>
            <div className="booth__cardfoot">
              No parent here and no signature ahead: “A parent signs for {first(picked.name)} — here
              at the booth. You can wait here, or we can call them.” Wording pending James and the
              parish’s advisor.
            </div>
          </section>
        ) : null}
        {!picked.quiet && !picked.whyHere && !isChair ? (
          <section className="booth__card booth__card--red" data-testid="booth-why">
            <div className="booth__cardhead">
              WHY ARE YOU HERE TODAY?
              <span className="booth__cardsub">
                asked once a year · they answer on the guest screen, or you pick here
              </span>
            </div>
            {(
              [
                ['parishioner', 'Parishioner', 'No commitment. Counted for the parish.'],
                ['community', 'Community member', 'No commitment.'],
                [
                  'school_family',
                  'School family — for our commitment',
                  'Credit goes to a family’s commitment. The family is linked before they check in.',
                ],
                [
                  'service_hours',
                  'Service hours',
                  'Not for a commitment. For which group — NHS, confirmation, a court or school program.',
                ],
              ] as const
            ).map(([key, label, says]) => (
              <div className="booth__row" key={key}>
                <span className="booth__rowa">
                  <b>{label}</b>
                </span>
                <span className="booth__rowc">
                  {says}
                  {key === 'service_hours' ? (
                    <>
                      {' '}
                      <input
                        className="booth__input"
                        aria-label="For which group"
                        placeholder="For which group"
                        value={serviceGroup}
                        onChange={(e) => {
                          setServiceGroup(e.target.value);
                        }}
                      />
                    </>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="booth__act"
                  disabled={busy || (key === 'service_hours' && !serviceGroup.trim())}
                  onClick={() => {
                    answerWhy(key);
                  }}
                >
                  Pick
                </button>
              </div>
            ))}
            {guest?.on ? (
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>Or they answer it</b>
                  <span className="booth__rowb">on the guest screen, in their words</span>
                </span>
                <span className="booth__rowc">
                  {guest.state === 'why'
                    ? 'On the guest screen now.'
                    : 'The same four answers, the same record.'}
                </span>
                <button
                  type="button"
                  className="booth__act"
                  onClick={() => {
                    show('why', picked.personId, expectedShift?.shiftId ?? null);
                  }}
                >
                  Ask on the guest screen
                </button>
              </div>
            ) : null}
            <div className="booth__cardfoot">
              This replaces “is this for the family?” as the first question. The day’s guards still
              run once a school family is linked.
            </div>
          </section>
        ) : null}
        {!picked.quiet && schoolFamily && !linkedFamily ? (
          <section className="booth__card booth__card--sky" data-testid="booth-which-family">
            <div className="booth__cardhead">
              WHICH FAMILY?
              <span className="booth__cardsub">CIDER suggests; they confirm</span>
            </div>
            {(choices ?? []).map((c) => (
              <div
                className={`booth__row${creditPick === c.familyId ? ' booth__row--ok' : ''}`}
                key={c.familyId}
              >
                <span className="booth__rowa">
                  <b>{c.responsibleParty}</b>
                  <span className="booth__rowb">{c.why ?? 'a family we hold'}</span>
                </span>
                <span className="booth__rowc">
                  Matched by {c.why === 'the same address' ? 'their home address' : 'the record'}.
                  Not linked yet.
                </span>
                <span className="booth__chip booth__chip--info">LIKELY</span>
                <button
                  type="button"
                  className="booth__act"
                  onClick={() => {
                    setCreditPick(c.familyId);
                  }}
                >
                  Link {first(picked.name)}
                </button>
              </div>
            ))}
            {choices?.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>No family we hold matches</b>
                  <span className="booth__rowb">
                    check in without a family; the booth chair links them
                  </span>
                </span>
              </div>
            ) : null}
          </section>
        ) : null}
        {!picked.quiet && schoolFamily && picked.earlier?.length ? (
          <section className="booth__card booth__card--gold" data-testid="booth-earlier">
            <div className="booth__cardhead">
              EARLIER THIS YEAR
              <span className="booth__cardsub">not credited because they weren’t linked</span>
            </div>
            {picked.earlier.map((e) => (
              <div className="booth__row booth__row--warn" key={e.checkInId}>
                <span className="booth__rowa">
                  <b>
                    {e.area} · {formatCountDay(e.day)} · {e.hours}
                  </b>
                  <span className="booth__rowb">checked in, no family</span>
                </span>
                <span className="booth__rowc">
                  Credit this to the family too, once they are linked.
                </span>
                <span className="booth__chip booth__chip--warn">UNLINKED</span>
                <button
                  type="button"
                  className="booth__act"
                  disabled={busy || !(linkedFamily ?? toLink)}
                  onClick={() => {
                    const fam = linkedFamily ?? toLink;
                    if (!fam) return;
                    act(
                      '/volunteers/booth/credit-earlier',
                      { checkInId: e.checkInId, familyId: fam.familyId },
                      (r) => {
                        setDone(r.says);
                        setPicked({
                          ...picked,
                          earlier: (picked.earlier ?? []).filter(
                            (x) => x.checkInId !== e.checkInId,
                          ),
                        });
                      },
                      true,
                    );
                  }}
                >
                  Credit it
                </button>
              </div>
            ))}
            <div className="booth__cardfoot">
              Linking is recorded with your name and “linked at the booth”. The family page shows it
              the same day, so the family can say if it’s wrong.
            </div>
          </section>
        ) : null}
        {picked.quiet ? null : (
          <div className="booth__bigrow">
            {expectedShift && !expectedShift.workedBy && !tooYoung && !isChair ? (
              <button
                type="button"
                className="booth__big"
                data-testid="booth-big"
                disabled={
                  busy ||
                  (needsWaiver && !signedHere) ||
                  (!!picked.minor && picked.emergency === false)
                }
                onClick={() => {
                  if (toLink) linkAndCheckIn(picked, expectedShift, toLink.familyId);
                  else checkIn(picked, expectedShift, false);
                }}
              >
                {toLink
                  ? `Link to ${toLink.responsibleParty}’s family and check in`
                  : `Check in ${first(picked.name)}`}
                <span className="booth__bigsub">
                  {expectedShift.area} · {hours(expectedShift.startsAt, expectedShift.endsAt)} ·{' '}
                  {tagless ? 'no tag' : tablet ? 'tag at the booth' : 'tag prints'} · Enter
                </span>
              </button>
            ) : inShift ? (
              <button
                type="button"
                className="booth__big booth__big--navy"
                data-testid="booth-big"
                disabled={busy}
                onClick={() => {
                  act(
                    '/volunteers/booth/check-out',
                    { shiftId: inShift.shiftId, personId: picked.personId },
                    (r) => {
                      setTag(null);
                      setDone(`${picked.name}: ${r.says}`);
                      show('thanks', picked.personId, inShift.shiftId);
                    },
                  );
                }}
              >
                Check {first(picked.name)} out of the {inShift.area}
                <span className="booth__bigsub">
                  {inShift.inAt
                    ? `${formatTime(inShift.inAt)} – ${formatTime(new Date())} · ${worked(inShift.inAt)}`
                    : `${inShift.area} · ${hours(inShift.startsAt, inShift.endsAt)}`}
                </span>
              </button>
            ) : null}
            {teenWaits ? (
              <button type="button" className="booth__side-act" onClick={reset}>
                {first(picked.name)} will wait for a parent
              </button>
            ) : null}
            {schoolFamily && !linkedFamily && expectedShift && !isChair ? (
              <button
                type="button"
                className="booth__side-act"
                disabled={busy}
                onClick={() => {
                  checkIn(picked, expectedShift, false, undefined, true);
                }}
              >
                Check in without a family
              </button>
            ) : null}
            {expectedShift?.signupId && !expectedShift.workedBy ? (
              <button
                type="button"
                className="booth__side-act"
                onClick={() => {
                  setHand({
                    kind: 'swap',
                    shiftId: expectedShift.shiftId,
                    signupId: expectedShift.signupId ?? '',
                  });
                }}
              >
                Not {first(picked.name)} — someone else is working it
              </button>
            ) : null}
            {inShift ? (
              <>
                <button
                  type="button"
                  className="booth__side-act"
                  disabled={busy}
                  onClick={() => {
                    reprint(picked, inShift);
                  }}
                >
                  Print the tag again
                </button>
                <button
                  type="button"
                  className="booth__side-act"
                  onClick={() => {
                    setWhy('');
                    setHand({ kind: 'undo', shiftId: inShift.shiftId });
                  }}
                >
                  Undo this check-in
                </button>
              </>
            ) : null}
            {(expectedShift ?? inShift) ? (
              <button
                type="button"
                className="booth__side-act"
                onClick={() => {
                  const s = expectedShift ?? inShift;
                  if (!s) return;
                  setWhy('');
                  setHand({
                    kind: 'reassign',
                    shiftId: s.shiftId,
                    to:
                      placeChoices.find((x) => x.shiftId !== s.shiftId && x.short > 0)?.shiftId ??
                      '',
                  });
                }}
              >
                Reassign…
              </button>
            ) : null}
            {!inShift && !isChair ? (
              <button
                type="button"
                className="booth__side-act"
                onClick={() => {
                  setHand({ kind: 'cover' });
                }}
              >
                Cover for someone…
              </button>
            ) : null}
            <button
              type="button"
              className="booth__side-act"
              data-testid="booth-hold-card"
              onClick={() => {
                show(holding ? 'welcome' : 'hold');
                setHolding(!holding);
              }}
            >
              {holding ? 'Let the guest screen go' : 'Hold the guest screen'}
            </button>
          </div>
        )}
        {picked.today.length === 0 && !picked.quiet && !isChair ? (
          <section className="booth__card booth__card--gold" data-testid="booth-wrongday">
            <div className="booth__cardhead">WHAT NOW</div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Help now</b>
                <span className="booth__rowb">standby</span>
              </span>
              <span className="booth__rowc">The booth chair sends them where it’s short.</span>
              <span className="booth__chip booth__chip--info">STANDBY</span>
              <button
                type="button"
                className="booth__act"
                disabled={busy}
                onClick={() => {
                  const who = picked;
                  act(
                    '/volunteers/booth/standby',
                    { personId: who.personId, note: why.trim() ? why : null },
                    (r) => {
                      setDone(r.says);
                    },
                  );
                }}
              >
                Put on standby
              </button>
            </div>
            <div className="booth__row booth__row--warn">
              <span className="booth__rowa">
                <b>A shift today</b>
                <span className="booth__rowb">
                  {shortest[0]
                    ? `${shortest[0].area} · ${hours(shortest[0].startsAt, shortest[0].endsAt)} is short ${String(shortest[0].short)}`
                    : 'nothing is short right now'}
                </span>
              </span>
              <span className="booth__rowc">
                {picked.otherDays.length
                  ? `Proceed anyway — your name and a reason are recorded. Their ${formatCountDay(picked.otherDays[0]?.day ?? '')} shift stays.`
                  : 'They join the shift you pick.'}
              </span>
              <span className="booth__chip booth__chip--warn">WARN</span>
            </div>
            <PlaceOn shifts={placeChoices} value={placeOn} onChange={setPlaceOn} />
            {picked.otherDays.length ? (
              <>
                <OperatorName value={operator} onChange={setOperator} desk={desk.name} />
                <label className="booth__row">
                  <span className="booth__k">WHY</span>
                  <input
                    className="booth__input"
                    aria-label="Why today"
                    value={why}
                    onChange={(e) => {
                      setWhy(e.target.value);
                    }}
                  />
                </label>
              </>
            ) : null}
            <div className="booth__row">
              <span className="booth__rowa">
                <b>
                  {picked.otherDays[0]
                    ? `Come back ${formatCountDay(picked.otherDays[0].day)}`
                    : 'Not today'}
                </b>
              </span>
              <span className="booth__rowc">Nothing recorded.</span>
            </div>
            <div className="booth__bigrow">
              <button
                type="button"
                className="booth__big"
                data-testid="booth-big"
                disabled={
                  busy ||
                  !placeOn ||
                  (picked.otherDays.length > 0 && (!opName.trim() || !why.trim()))
                }
                onClick={() => {
                  const s = placeChoices.find((x) => x.shiftId === placeOn);
                  if (s)
                    checkIn(
                      picked,
                      s,
                      true,
                      picked.otherDays.length ? { name: opName, reason: why } : undefined,
                    );
                }}
              >
                Check in {first(picked.name)} here
                <span className="booth__bigsub">on the shift you picked</span>
              </button>
            </div>
          </section>
        ) : null}
        {isChair && !picked.quiet ? (
          <section className="booth__card booth__card--ok" data-testid="booth-chair">
            <div className="booth__cardhead">
              {picked.name.toUpperCase()}
              <span className="booth__cardsub">
                {picked.chairOf?.length ? `${picked.chairOf.join(' · ')} chair` : 'chair this year'}
              </span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Helping today</b>
                <span className="booth__rowb">
                  → {picked.chairOf?.length ? picked.chairOf.join(' · ') : 'their area'}
                </span>
              </span>
              <span className="booth__rowc">
                Chairs do not check in at the booth. The radio and the keys are issued here, on the
                key log, with your name.
              </span>
              <span className="booth__chip booth__chip--ok">CHAIR</span>
            </div>
          </section>
        ) : null}
        {picked.issue.eligible && !picked.quiet && !tablet ? (
          <div className="booth__card booth__card--sky" data-testid="booth-issue">
            <div className="booth__cardhead">
              ISSUE
              <span className="booth__cardsub">one tap each · names you as the operator</span>
            </div>
            {picked.issue.out.length === 0 ? (
              <span className="booth__fine">Nothing out.</span>
            ) : (
              picked.issue.out.map((o) => (
                <div className="booth__row" key={o.id}>
                  <span>
                    {o.itemName} {o.number} · still out
                  </span>
                  <button
                    type="button"
                    className="booth__btn"
                    disabled={busy}
                    onClick={() => {
                      const who = picked;
                      act('/volunteers/booth/return', { custodyId: o.id }, (r) => {
                        setDone(`${who.name}: ${r.says}`);
                      });
                    }}
                  >
                    Returned
                  </button>
                </div>
              ))
            )}
            <div className="booth__row">
              <select
                className="booth__input"
                aria-label="What"
                value={issueItem}
                onChange={(e) => {
                  setIssueItem(e.target.value === 'key' ? 'key' : 'radio');
                }}
              >
                <option value="radio">Radio</option>
                <option value="key">Key</option>
              </select>
              <input
                className="booth__input"
                aria-label="Number"
                placeholder="Number"
                value={issueNumber}
                onChange={(e) => {
                  setIssueNumber(e.target.value);
                }}
              />
            </div>
            <OperatorName value={operator} onChange={setOperator} desk={desk.name} />
            <button
              type="button"
              className="booth__btn"
              disabled={busy || !issueNumber.trim() || !opName.trim()}
              onClick={() => {
                const who = picked;
                act(
                  '/volunteers/booth/issue',
                  {
                    personId: who.personId,
                    item: issueItem,
                    number: issueNumber,
                    name: opName,
                  },
                  (r) => {
                    setIssueNumber('');
                    setDone(`${who.name}: ${r.says}`);
                  },
                );
              }}
            >
              Issue it
            </button>
            <div className="booth__cardfoot">
              Volunteers are not issued anything. This shows only for chairs. Radios and keys come
              back with “Returned”.
            </div>
          </div>
        ) : null}
        <button type="button" className="booth__link" onClick={reset}>
          Back to search
        </button>
      </div>
    </>
  );
}
